import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import {
  cannedResponsesService,
  type CannedResponse as ApiCannedResponse,
} from '@services/canned-responses.service';

export interface CannedResponse {
  id: string;
  name: string;
  shortcut: string; // e.g., "/thanks" or "#greeting"
  content: string;
  category: string;
  variables?: string[]; // e.g., ['customer_name', 'ticket_number']
  isPrivate: boolean; // personal vs team-shared
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  usageCount: number;
}

export interface CannedResponseCategory {
  id: string;
  name: string;
  color: string;
}

interface CannedResponsesState {
  responses: CannedResponse[];
  categories: CannedResponseCategory[];
  isLoading: boolean;
  error: string | null;
  searchQuery: string;
  selectedCategory: string | null;

  // Actions
  setResponses: (responses: CannedResponse[]) => void;
  /** Load the tenant's canned responses from the API into the picker (#18938). */
  fetchResponses: () => Promise<void>;
  addResponse: (response: Omit<CannedResponse, 'id' | 'createdAt' | 'updatedAt' | 'usageCount'>) => void;
  updateResponse: (id: string, updates: Partial<CannedResponse>) => void;
  deleteResponse: (id: string) => void;
  incrementUsage: (id: string) => void;
  setCategories: (categories: CannedResponseCategory[]) => void;
  addCategory: (category: Omit<CannedResponseCategory, 'id'>) => void;
  deleteCategory: (id: string) => void;
  setSearchQuery: (query: string) => void;
  setSelectedCategory: (categoryId: string | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  getFilteredResponses: () => CannedResponse[];
  findByShortcut: (shortcut: string) => CannedResponse | undefined;
}

const DEFAULT_CATEGORIES: CannedResponseCategory[] = [
  { id: 'general', name: 'General', color: 'bg-gray-100 text-gray-700' },
  { id: 'greeting', name: 'Greetings', color: 'bg-blue-100 text-blue-700' },
  { id: 'closing', name: 'Closings', color: 'bg-green-100 text-green-700' },
  { id: 'technical', name: 'Technical', color: 'bg-purple-100 text-purple-700' },
  { id: 'billing', name: 'Billing', color: 'bg-yellow-100 text-yellow-700' },
  { id: 'escalation', name: 'Escalation', color: 'bg-red-100 text-red-700' },
];

const SAMPLE_RESPONSES: CannedResponse[] = [
  {
    id: '1',
    name: 'Thank You',
    shortcut: '/thanks',
    content: 'Thank you for contacting us, {{customer_name}}! We appreciate your patience and are happy to help.',
    category: 'general',
    variables: ['customer_name'],
    isPrivate: false,
    createdBy: 'system',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    usageCount: 0,
  },
  {
    id: '2',
    name: 'Greeting',
    shortcut: '/hi',
    content: 'Hello {{customer_name}},\n\nThank you for reaching out to our support team. I\'m here to help you with your inquiry.',
    category: 'greeting',
    variables: ['customer_name'],
    isPrivate: false,
    createdBy: 'system',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    usageCount: 0,
  },
  {
    id: '3',
    name: 'Closing - Resolved',
    shortcut: '/resolved',
    content: 'I\'m glad we could resolve your issue today! If you have any other questions, please don\'t hesitate to reach out.\n\nBest regards,\n{{agent_name}}',
    category: 'closing',
    variables: ['agent_name'],
    isPrivate: false,
    createdBy: 'system',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    usageCount: 0,
  },
  {
    id: '4',
    name: 'Need More Info',
    shortcut: '/moreinfo',
    content: 'Thank you for reaching out. To better assist you with ticket #{{ticket_number}}, could you please provide the following information:\n\n1. \n2. \n3. \n\nThis will help us resolve your issue more quickly.',
    category: 'general',
    variables: ['ticket_number'],
    isPrivate: false,
    createdBy: 'system',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    usageCount: 0,
  },
  {
    id: '5',
    name: 'Escalation Notice',
    shortcut: '/escalate',
    content: 'I understand your concern and want to ensure you receive the best possible support. I\'m escalating your case to our senior team who will review it and get back to you within {{timeframe}}.\n\nYour ticket number for reference: #{{ticket_number}}',
    category: 'escalation',
    variables: ['timeframe', 'ticket_number'],
    isPrivate: false,
    createdBy: 'system',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    usageCount: 0,
  },
];

export const useCannedResponsesStore = create<CannedResponsesState>()(
  persist(
    (set, get) => ({
      responses: SAMPLE_RESPONSES,
      categories: DEFAULT_CATEGORIES,
      isLoading: false,
      error: null,
      searchQuery: '',
      selectedCategory: null,

      setResponses: (responses) => set({ responses }),

      // #18938 — the picker used to show only the hardcoded SAMPLE_RESPONSES;
      // the tenant's responses from GET /api/canned-responses never reached it.
      // Load them and replace the placeholders, mapping the API shape onto the
      // picker's (shortcut/category/createdBy are optional on the API).
      fetchResponses: async () => {
        set({ isLoading: true, error: null });
        try {
          const apiList = await cannedResponsesService.getAll();
          const mapped: CannedResponse[] = (apiList || [])
            .filter((r: ApiCannedResponse) => r.isActive !== false)
            .map((r: ApiCannedResponse) => ({
              id: r.id,
              name: r.name,
              shortcut: r.shortcut || '',
              content: r.content,
              category: r.category || 'general',
              variables: r.variables,
              isPrivate: r.isPrivate,
              createdBy: r.createdBy || '',
              createdAt: r.createdAt,
              updatedAt: r.updatedAt,
              usageCount: r.usageCount ?? 0,
            }));
          set({ responses: mapped, isLoading: false });
        } catch (err: any) {
          set({
            isLoading: false,
            error: err?.response?.data?.message || err?.message || 'Failed to load canned responses',
          });
        }
      },

      addResponse: (response) => {
        const newResponse: CannedResponse = {
          ...response,
          id: Math.random().toString(36).substr(2, 9),
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          usageCount: 0,
        };
        set((state) => ({
          responses: [...state.responses, newResponse],
        }));
      },

      updateResponse: (id, updates) => {
        set((state) => ({
          responses: state.responses.map((r) =>
            r.id === id ? { ...r, ...updates, updatedAt: new Date().toISOString() } : r
          ),
        }));
      },

      deleteResponse: (id) => {
        set((state) => ({
          responses: state.responses.filter((r) => r.id !== id),
        }));
      },

      incrementUsage: (id) => {
        set((state) => ({
          responses: state.responses.map((r) =>
            r.id === id ? { ...r, usageCount: r.usageCount + 1 } : r
          ),
        }));
      },

      setCategories: (categories) => set({ categories }),

      addCategory: (category) => {
        const newCategory: CannedResponseCategory = {
          ...category,
          id: Math.random().toString(36).substr(2, 9),
        };
        set((state) => ({
          categories: [...state.categories, newCategory],
        }));
      },

      deleteCategory: (id) => {
        set((state) => ({
          categories: state.categories.filter((c) => c.id !== id),
        }));
      },

      setSearchQuery: (query) => set({ searchQuery: query }),

      setSelectedCategory: (categoryId) => set({ selectedCategory: categoryId }),

      setLoading: (loading) => set({ isLoading: loading }),

      setError: (error) => set({ error }),

      getFilteredResponses: () => {
        const { responses, searchQuery, selectedCategory } = get();
        return responses.filter((r) => {
          const matchesSearch =
            !searchQuery ||
            r.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            r.shortcut.toLowerCase().includes(searchQuery.toLowerCase()) ||
            r.content.toLowerCase().includes(searchQuery.toLowerCase());
          const matchesCategory = !selectedCategory || r.category === selectedCategory;
          return matchesSearch && matchesCategory;
        });
      },

      findByShortcut: (shortcut) => {
        const { responses } = get();
        return responses.find(
          (r) => r.shortcut.toLowerCase() === shortcut.toLowerCase()
        );
      },
    }),
    {
      name: 'centraoneplus-canned-responses',
    }
  )
);

// Utility function to replace variables in content
export function replaceVariables(
  content: string,
  variables: Record<string, string>
): string {
  let result = content;
  for (const [key, value] of Object.entries(variables)) {
    result = result.replace(new RegExp(`{{${key}}}`, 'g'), value);
  }
  return result;
}

// Extract variable names from content
export function extractVariables(content: string): string[] {
  const regex = /{{(\w+)}}/g;
  const matches: string[] = [];
  let match;
  while ((match = regex.exec(content)) !== null) {
    if (!matches.includes(match[1])) {
      matches.push(match[1]);
    }
  }
  return matches;
}
